import type {
  MarkdownAstNode,
  ParsedMarkdownAst,
} from '@/lib/markdown/parse-markdown-ast'

const identifierOf = (node: MarkdownAstNode) =>
  String(node.identifier ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase()

const nodeText = (node: MarkdownAstNode): string => {
  if (node.type === 'image' || node.type === 'imageReference')
    return node.alt ?? ''
  if (node.type === 'html' || node.type === 'footnoteReference') return ''
  return node.value ?? node.children?.map(nodeText).join('') ?? ''
}

// 只接受独立的空锚点；其他 HTML 仍由原有导出逻辑处理。
const explicitAnchor = (node: MarkdownAstNode) => {
  if (node.type !== 'html') return undefined
  const match = node.value?.match(
    /^\s*<a\s+(?:id|name)\s*=\s*(?:"([^"]+)"|'([^']+)'|([^\s>]+))\s*>\s*(?:<\/a>\s*)?$/i,
  )
  return match ? (match[1] ?? match[2] ?? match[3]) : undefined
}

export const isSafeWordHyperlink = (href: string) => {
  try {
    return ['http:', 'https:', 'mailto:', 'tel:', 'ftp:'].includes(
      new URL(href).protocol,
    )
  } catch {
    return false
  }
}

export type WordReferenceDocument = {
  body: ParsedMarkdownAst
  notes: Map<number, ParsedMarkdownAst>
}

// 先索引整份 AST，再适配引用，支持前向引用且不改变预览使用的语法树。
export const prepareWordReferences = (
  parsed: ParsedMarkdownAst,
): WordReferenceDocument => {
  const definitions = new Map<string, MarkdownAstNode>()
  const notes = new Map<string, MarkdownAstNode>()
  const anchors = new Map<string, string>()
  const nodeBookmarks = new Map<MarkdownAstNode, string>()
  const usedSlugs = new Set<string>()
  const noteIds = new Map<string, number>()

  const walk = (
    node: MarkdownAstNode,
    visit: (node: MarkdownAstNode) => void,
    skipNotes = false,
  ) => {
    if (skipNotes && node.type === 'footnoteDefinition') return
    visit(node)
    node.children?.forEach((child) => walk(child, visit, skipNotes))
  }

  walk(parsed.root, (node) => {
    const identifier = identifierOf(node)
    if (node.type === 'definition' && !definitions.has(identifier)) {
      definitions.set(identifier, node)
    }
    if (node.type === 'footnoteDefinition' && !notes.has(identifier)) {
      notes.set(identifier, node)
    }
  })

  const registerAnchors = (root: MarkdownAstNode) => {
    walk(
      root,
      (node) => {
        let anchor = explicitAnchor(node)
        if (node.type === 'heading') {
          const base = nodeText(node)
            .toLowerCase()
            .replace(new RegExp('[^\\p{L}\\p{N}\\p{M}_\\-\\s]', 'gu'), '')
            .replace(/\s/g, '-')
          anchor = base
          let suffix = 0
          while (usedSlugs.has(anchor)) anchor = `${base}-${++suffix}`
          usedSlugs.add(anchor)
        }
        if (anchor === undefined) return
        // Word 书签使用简短的 ASCII 名称，Markdown 锚点只用于查询映射。
        const bookmark = `md_anchor_${nodeBookmarks.size + 1}`
        nodeBookmarks.set(node, bookmark)
        if (!anchors.has(anchor)) anchors.set(anchor, bookmark)
      },
      true,
    )
  }

  walk(
    parsed.root,
    (node) => {
      if (node.type !== 'footnoteReference') return
      const identifier = identifierOf(node)
      if (notes.has(identifier) && !noteIds.has(identifier)) {
        noteIds.set(identifier, noteIds.size + 1)
      }
    },
    true,
  )
  registerAnchors(parsed.root)
  for (const identifier of Array.from(noteIds.keys())) {
    registerAnchors({ type: 'root', children: notes.get(identifier)!.children })
  }

  const noteReferences = new Map<number, string[]>()
  const transform = (
    node: MarkdownAstNode,
    inNote = false,
  ): MarkdownAstNode => {
    if (node.type === 'definition' || node.type === 'footnoteDefinition') {
      return { ...node, children: [] }
    }
    if (node.type === 'linkReference' || node.type === 'imageReference') {
      const definition = definitions.get(identifierOf(node))
      if (definition) {
        return transform(
          {
            ...node,
            type: node.type === 'linkReference' ? 'link' : 'image',
            url: definition.url,
          },
          inNote,
        )
      }
    }
    const children = node.children
      ?.filter(
        (child, index, siblings) =>
          !(
            child.type === 'html' &&
            /^\s*<\/a>\s*$/i.test(child.value ?? '') &&
            index > 0 &&
            explicitAnchor(siblings[index - 1]) !== undefined
          ),
      )
      .map((child) => transform(child, inNote))
    const bookmark = nodeBookmarks.get(node)
    if (bookmark) {
      const marker = { type: 'wordBookmark', value: '', bookmark }
      if (node.type === 'heading') {
        return { ...node, children: [marker, ...(children ?? [])] }
      }
      return { ...node, type: 'paragraph', children: [marker] }
    }
    if (node.type === 'footnoteReference') {
      const noteId = noteIds.get(identifierOf(node))
      // 注释中的引用复用正文已经分配的编号，避免循环展开注释。
      if (noteId === undefined) {
        return { type: 'text', value: `[^${node.label ?? node.identifier}]` }
      }
      const references = noteReferences.get(noteId) ?? []
      const referenceBookmark = inNote
        ? undefined
        : `md_note_ref_${noteId}_${references.length + 1}`
      if (referenceBookmark) {
        references.push(referenceBookmark)
        noteReferences.set(noteId, references)
      }
      return {
        ...node,
        type: 'wordFootnoteReference',
        noteId,
        referenceBookmark,
      }
    }
    if (node.type === 'link' && node.url?.startsWith('#')) {
      let anchor = node.url.slice(1)
      try {
        anchor = decodeURIComponent(anchor)
      } catch {
        // 不合法的百分号转义按原始锚点查找，不中断导出。
      }
      return { ...node, children, bookmark: anchors.get(anchor) }
    }
    return { ...node, children }
  }

  const body = { ...parsed, root: transform(parsed.root) }
  const endNotes = new Map<number, ParsedMarkdownAst>()
  for (const [identifier, id] of Array.from(noteIds)) {
    const children = (notes.get(identifier)!.children ?? []).map((node) =>
      transform(node, true),
    )
    if (children[0]?.type !== 'paragraph' && children[0]?.type !== 'heading') {
      children.unshift({ type: 'paragraph', children: [] })
    }
    const first = children[0]
    const references = noteReferences.get(id) ?? []
    // 书签覆盖注释文字，避免空书签在不同办公软件中被忽略。
    const marker: MarkdownAstNode = {
      type: 'wordBookmark',
      bookmark: `md_note_${id}`,
      children: [
        { type: 'text', value: `[${id}] ` },
        ...(first.children ?? []),
      ],
    }
    const backlinks: MarkdownAstNode[] = references.flatMap((name, index) => [
      { type: 'text', value: ' ' },
      {
        type: 'link',
        url: `#${name}`,
        bookmark: name,
        children: [
          {
            type: 'text',
            value: references.length > 1 ? `↩${index + 1}` : '↩',
          },
        ],
      },
    ])
    children[0] = { ...first, children: [marker, ...backlinks] }
    endNotes.set(id, { ...parsed, root: { type: 'root', children } })
  }
  return { body, notes: endNotes }
}
